import type { ApolloClient } from '@apollo/client';
import type { FragmentType } from 'src/gql';
import type { ClientContact } from 'src/types/client';
import { useApolloClient } from '@apollo/client';
import { datadogRum } from '@datadog/browser-rum';
import { ButtonGroup } from '@savvy-kit/components/button-group';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@savvy-kit/components/dropdown-menu';
import { isNil } from 'es-toolkit';
import { ChevronDown, SquareArrowOutUpRight } from 'lucide-react';
import { TrackedButton, TrackedDropdownMenuItem, TrackedIconButton } from 'src/components/tracked';
import { graphql, unmaskFragment } from 'src/gql';
import { handleMutationError } from 'src/utils/handle-mutation-error';
import { DatadogRumAction } from 'src/utils/logging';

import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';
import { addLoggedInAsAdvisorID } from '@savvy/deprecated-shared/utils/savvy-auth';
import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

import { useApolloNetworkQuiet } from './use-apollo-network-quiet';

const IMPERSONATE_USER = graphql(/* GraphQL */ `
  mutation LoginAsClientButtonImpersonateUser($input: ImpersonateUserInput!) {
    impersonateUser(input: $input) {
      currentUser {
        id
        email
      }
      trueUser {
        id
        email
      }
    }
  }
`);

export const LogInAsClientButtonFragment = graphql(/* GraphQL */ `
  fragment LogInAsClientButton on Client {
    user {
      role
    }
  }
`);

// Kept outside the hook: react-compiler rejects a global write inside a component or hook.
function reloadAsImpersonatedUser() {
  window.location.href = '/';
}

type ImpersonationArgs = {
  readonly clientFrag: FragmentType<typeof LogInAsClientButtonFragment> | undefined;
  readonly contact: ClientContact;
  readonly disabled: boolean;
  readonly pageReady: boolean;
  /** Supply this only from a caller that unmounts on click, taking `useMutationWithErrorLogging`'s
   * own error effect with it. A caller that stays mounted already reports through that effect, and
   * reporting again here would double-log to Datadog and add a toast it never had. */
  readonly onFailure?: (error: unknown, target: ClientContact) => void;
};

function useImpersonation({ clientFrag, contact, disabled, pageReady, onFailure }: ImpersonationArgs) {
  const client = unmaskFragment(LogInAsClientButtonFragment, clientFrag);
  const apolloClient: ApolloClient<object> = useApolloClient();
  const { user: advisorAuthUser } = useAuth();

  const [impersonateUser] = useMutationWithErrorLogging(IMPERSONATE_USER);

  const isDisabled =
    disabled || !contact.Savvy_User_Id__c || isNil(client) || client.user.role === 'NON_ADVISORY_USER' || !pageReady;

  const handleClick = async (target: ClientContact) => {
    if (isDisabled) return;
    datadogRum.addAction(DatadogRumAction.LoginAsClicked, {
      userId: target.Savvy_User_Id__c,
    });
    try {
      // Cancel in-flight queries first so their _interslice_session responses
      // can't clobber the impersonation cookie set by the mutation below.
      await apolloClient.clearStore();
      await impersonateUser({
        variables: {
          input: {
            id: target.Savvy_User_Id__c,
          },
        },
      });
    } catch (error) {
      onFailure?.(error, target);
      return;
    }
    addLoggedInAsAdvisorID(advisorAuthUser?.id || '');
    reloadAsImpersonatedUser();
  };

  return { handleClick, isDisabled };
}

type LogInAsButtonProps = {
  readonly clientFrag: FragmentType<typeof LogInAsClientButtonFragment> | undefined;
  readonly contact: ClientContact;
  readonly co_contact?: ClientContact | null;
  readonly disabled?: boolean;
};
function LogInAsClientButton({ clientFrag, contact, co_contact, disabled = false }: LogInAsButtonProps) {
  const pageReady = useApolloNetworkQuiet();
  const { handleClick, isDisabled } = useImpersonation({ clientFrag, contact, disabled, pageReady });

  return (
    <ButtonGroup orientation="horizontal">
      <TrackedButton
        size="sm"
        variant="secondary"
        elementName="login-as-client-button"
        icon={<SquareArrowOutUpRight />}
        disabled={isDisabled}
        onClick={() => handleClick(contact)}
        className="py-0"
      >
        {`Log in as ${contact.FirstName}`}
      </TrackedButton>
      {co_contact && (
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <TrackedIconButton
                size="sm"
                elementName="login-as-client-button-dropdown"
                disabled={isDisabled}
                variant="secondary"
              />
            }
          >
            <ChevronDown />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleClick(co_contact)}>
              <SquareArrowOutUpRight />
              <span>{`Log in as ${co_contact.FirstName}`}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </ButtonGroup>
  );
}
export default LogInAsClientButton;

type LogInAsClientMenuItemProps = LogInAsButtonProps & {
  readonly pageReady: boolean;
};

function reportImpersonationFailure(error: unknown, target: ClientContact) {
  datadogRum.addError(error, { target_user_id: target.Savvy_User_Id__c });
  handleMutationError(error, 'Could not log in as this client. Please try again.');
}

/**
 * Menu-item form of the control above, for the household header's overflow menu.
 * A joint household gets a submenu rather than a nested `DropdownMenu`, which
 * `DropdownMenuItem`'s close-on-click default would tear down as it opened.
 */
export function LogInAsClientMenuItem({
  clientFrag,
  contact,
  co_contact,
  disabled = false,
  pageReady,
}: LogInAsClientMenuItemProps) {
  const { handleClick, isDisabled } = useImpersonation({
    clientFrag,
    contact,
    disabled,
    pageReady,
    onFailure: reportImpersonationFailure,
  });

  if (isNil(co_contact)) {
    return (
      <TrackedDropdownMenuItem
        elementName="login-as-client-menu-item"
        disabled={isDisabled}
        onClick={() => handleClick(contact)}
      >
        <SquareArrowOutUpRight />
        <span>{`Log in as ${contact.FirstName}`}</span>
      </TrackedDropdownMenuItem>
    );
  }

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger
        disabled={isDisabled}
        className="data-disabled:pointer-events-none data-disabled:opacity-50"
      >
        <SquareArrowOutUpRight />
        <span>Log in as client</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        <TrackedDropdownMenuItem
          elementName="login-as-client-menu-item"
          disabled={isDisabled}
          onClick={() => handleClick(contact)}
        >
          <span>{`Log in as ${contact.FirstName}`}</span>
        </TrackedDropdownMenuItem>
        <TrackedDropdownMenuItem
          elementName="login-as-co-client-menu-item"
          disabled={isDisabled}
          onClick={() => handleClick(co_contact)}
        >
          <span>{`Log in as ${co_contact.FirstName}`}</span>
        </TrackedDropdownMenuItem>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
